/**
 * CCW Duplicate Guard (CHR-13/14)
 *
 * Keeps the product-page duplicate-purchase warning in sync with the cart after
 * AJAX changes (theme cart events + any /cart/*.js mutation, including ones made
 * by the UpCart drawer). Initial state is server-rendered by the
 * 'ccw-duplicate-warning' snippet; the cart page re-renders itself through the
 * theme's own section refresh, so this module only manages the product page.
 *
 * Debug: window.CCWDuplicateGuard.state / .check(productId) / .refresh()
 */
(function () {
  'use strict';

  var SECTION_ID = 'ccw-bundle-map';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var state = { bundles: [], itemProductIds: [], items: [] };
  var refreshTimer = null;
  var drawerTimer = null;
  var drawerObserver = null;

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root;
  }

  function findBundleFor(productId) {
    var id = Number(productId);
    for (var i = 0; i < state.bundles.length; i++) {
      if (state.bundles[i].includes.indexOf(id) !== -1) return state.bundles[i];
    }
    return null;
  }

  function applyToProductPage() {
    var el = document.querySelector('[data-ccw-duplicate-warning]');
    if (!el) return;
    var bundle = findBundleFor(el.getAttribute('data-ccw-product-id'));
    if (bundle) {
      var title = el.querySelector('[data-ccw-bundle-title]');
      if (title) title.textContent = bundle.title;
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  /* ---- UpCart drawer injection (CHR-16) ----
   * The cart drawer is the UpCart app embed, rendered inside the open shadow
   * root of #upCart. We append a small flag under the quantity controls of any
   * line item that a cart bundle already includes. Everything here is
   * defensive: if UpCart changes its markup, the flag silently doesn't render.
   */
  var DRAWER_STYLE = '.ccw-upcart-flag{display:flex;align-items:flex-start;gap:6px;margin-top:8px;padding:7px 9px;border:1px solid #e6b800;border-left-width:3px;border-radius:5px;background:#fff8e1;color:#5c4a00;font-size:12px;line-height:1.4;}.ccw-upcart-flag svg{flex:0 0 auto;width:14px;height:14px;margin-top:1px;color:#b38f00;}';
  var FLAG_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>';

  function flaggedHandles() {
    var map = {};
    for (var i = 0; i < state.items.length; i++) {
      var item = state.items[i];
      var bundle = findBundleFor(item.productId);
      if (bundle) map[item.handle] = bundle.title;
    }
    return map;
  }

  function handleFromHref(href) {
    var m = /\/products\/([^/?#]+)/.exec(href || '');
    return m ? m[1] : null;
  }

  function injectDrawerFlags() {
    var host = document.getElementById('upCart');
    var root = host && host.shadowRoot;
    if (!root) return;

    if (!root.querySelector('#ccw-upcart-style')) {
      var style = document.createElement('style');
      style.id = 'ccw-upcart-style';
      style.textContent = DRAWER_STYLE;
      root.appendChild(style);
    }

    var flagged = flaggedHandles();
    var rows = root.querySelectorAll('li.upcart-internal-cart-item, li.upcart-public-cart-item');
    rows.forEach(function (row) {
      var link = row.querySelector('a[href*="/products/"]');
      var handle = link && handleFromHref(link.getAttribute('href'));
      var bundleTitle = handle && flagged[handle];
      var existing = row.querySelector('.ccw-upcart-flag');
      if (bundleTitle) {
        var text = 'Heads up: the ' + bundleTitle + ' in your cart already includes this part.';
        if (existing) {
          if (existing.getAttribute('data-ccw-text') !== text) {
            existing.setAttribute('data-ccw-text', text);
            existing.querySelector('span').textContent = text;
          }
          return;
        }
        var flag = document.createElement('div');
        flag.className = 'ccw-upcart-flag';
        flag.setAttribute('data-ccw-text', text);
        flag.innerHTML = FLAG_ICON + '<span></span>';
        flag.querySelector('span').textContent = text;
        var info = row.querySelector('[class*="product-tile__info"]') || row;
        info.appendChild(flag);
      } else if (existing) {
        existing.remove();
      }
    });
  }

  function scheduleDrawerInject() {
    clearTimeout(drawerTimer);
    drawerTimer = setTimeout(injectDrawerFlags, 150);
  }

  function watchDrawer() {
    var host = document.getElementById('upCart');
    if (!host) return void setTimeout(watchDrawer, 2000);
    if (!host.shadowRoot) return void setTimeout(watchDrawer, 1000);
    if (drawerObserver) return;
    drawerObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        // ignore mutations caused by our own flags
        if (t && t.nodeType === 1 && t.closest && t.closest('.ccw-upcart-flag')) continue;
        var added = mutations[i].addedNodes;
        var ours = added.length === 1 && added[0].nodeType === 1 && added[0].classList && added[0].classList.contains('ccw-upcart-flag');
        if (!ours) return scheduleDrawerInject();
      }
    });
    drawerObserver.observe(host.shadowRoot, { childList: true, subtree: true });
    scheduleDrawerInject();
  }

  function refresh() {
    return fetch(rootUrl() + '?sections=' + SECTION_ID)
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        var html = sections[SECTION_ID];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var json = doc.querySelector('[data-ccw-bundle-map]');
        if (!json) return;
        state = JSON.parse(json.textContent);
        applyToProductPage();
        injectDrawerFlags();
      })
      .catch(function () { /* leave last known state in place */ });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 400);
  }

  // Theme pub/sub (Minimog) — fires for the theme's own cart operations.
  if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, scheduleRefresh);
  }
  document.addEventListener('cart:updated', scheduleRefresh);

  // Network-level fallback — catches cart mutations from app embeds (e.g. UpCart).
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = origFetch.apply(this, arguments);
    if (CART_MUTATION_RE.test(url)) {
      result.then(scheduleRefresh, function () {});
    }
    return result;
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (url && CART_MUTATION_RE.test(String(url))) {
      this.addEventListener('loadend', scheduleRefresh);
    }
    return origOpen.apply(this, arguments);
  };

  // Seed state once so the debug API works before any cart mutation.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(); watchDrawer(); });
  } else {
    refresh();
    watchDrawer();
  }

  window.CCWDuplicateGuard = {
    get state() { return state; },
    check: function (productId) { return findBundleFor(productId); },
    refresh: refresh,
  };
})();
